import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useCallback, useEffect, useMemo } from "react";
import useGetDailyFormInspections from "./useGetDailyFormInspections";
import MDTypography from "components/MDTypography";
import DailyInspectionActionsCell from "components/fleet/DailyInspectionActionsCell";
import dayjs from "dayjs";
import { QueryDailyFormInspectionsSortKey, SortDirection } from "generated/graphql";
import { sortDirectionMap } from "constants/sortDirectionMap";
import useGetDailyFormVehicles from "./useGetDailyFormVehicles";
import MultipleChoiceFilter from "components/filters/MultipleChoiceFilter/MultipleChoiceFilter";
import DateFilter from "components/filters/DateFilter/DateFilter";

export const dailyInspectionsSortKeyMap = {
  createdAt: QueryDailyFormInspectionsSortKey.CREATED_AT,
  "dailyFormVehicle.name": QueryDailyFormInspectionsSortKey.DAILY_FORM_VEHICLE_NAME,
  id: QueryDailyFormInspectionsSortKey.ID,
};

export const dailyInspectionsInitialSortDirectionKey = "desc";
export const dailyInspectionsInitialSortDirectionValue = SortDirection.DESC;
export const dailyInspectionsInitialSortKeyKey = "createdAt";
export const dailyInspectionsInitialSortKeyValue = QueryDailyFormInspectionsSortKey.CREATED_AT;

export default function useDailyInspectionsTable(onEditClick?: (inspectionId: string) => void) {
  const { filtering, sorting, offset } = usePaginatedVariables();

  const [getDailyFormVehicles, { data: vehiclesData }] = useGetDailyFormVehicles();

  const params = {
    first: offset.first,
    page: offset.page,
    search: filtering.debouncedSearchTerm,
    sortDirection:
      sortDirectionMap[sorting.sortDirection] || dailyInspectionsInitialSortDirectionValue,
    sortKey: dailyInspectionsSortKeyMap[sorting.sortKey] || dailyInspectionsInitialSortKeyValue,
    dailyFormVehicleId: filtering.multipleUserIDs?.[0] || undefined,
    dateSubmitted: filtering.dueAt ? `${filtering.dueAt} 00:00:00` : undefined,
  };

  const { data, loading, error, paginatorInfo } = useGetDailyFormInspections({
    variables: params,
  });

  useEffect(() => {
    getDailyFormVehicles({ first: 1000, page: 1 });
  }, [getDailyFormVehicles]);

  const pagination = usePaginatedResources({
    paginatorInfo,
    paginate: offset.paginate,
  });

  const handleVehicleChecked = useCallback(
    (selectedOptions) => {
      return filtering.setMultipleUserIDs(selectedOptions);
    },
    [filtering]
  );

  const VehicleFilter = useCallback(
    ({ column: { id } }) => {
      return (
        <MultipleChoiceFilter
          id={id}
          value={filtering.multipleUserIDs}
          onChange={handleVehicleChecked}
          options={
            vehiclesData?.map((vehicle) => ({
              value: vehicle.id,
              label: vehicle.name,
            })) || []
          }
        />
      );
    },
    [filtering.multipleUserIDs, handleVehicleChecked, vehiclesData]
  );

  const DateSubmittedFilter = useCallback(
    ({ column: { id } }) => {
      return <DateFilter id={id} value={filtering.dueAt} onChange={filtering.setDueAt} />;
    },
    [filtering.dueAt, filtering.setDueAt]
  );

  const columns = useMemo(() => {
    return [
      {
        Header: "Date Submitted",
        accessor: "createdAt",
        width: "15%",
        sortType: "datetime",
        Filter: DateSubmittedFilter,
        Cell: ({ value }: any) => (
          <MDTypography variant="body">
            {value ? dayjs(value).format("MM/DD/YYYY HH:mm") : "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Vehicle",
        accessor: "dailyFormVehicle.name",
        width: "20%",
        sortType: "basic",
        Filter: VehicleFilter,
        Cell: ({ value }: any) => (
          <MDTypography variant="body" fontWeight="medium">
            {value || "-"}
          </MDTypography>
        ),
      },
      {
        Header: "Submitter",
        accessor: "user.name",
        width: "20%",
        disableSortBy: true,
        Cell: ({ value }: any) => <MDTypography variant="body">{value || "-"}</MDTypography>,
      },
      {
        Header: "Mileage",
        accessor: "vehicleMilleage",
        width: "10%",
        disableSortBy: true,
        Cell: ({ value }: any) => (
          <MDTypography variant="body">{value ? `${value} mi` : "-"}</MDTypography>
        ),
      },
      {
        align: "center",
        Header: "Actions",
        accessor: "actions",
        disableSortBy: true,
        Cell: (props: any) => <DailyInspectionActionsCell {...props} onEditClick={onEditClick} />,
      },
    ];
  }, [DateSubmittedFilter, VehicleFilter, onEditClick]);

  const tableData = useMemo(() => {
    return {
      columns,
      rows: data || [],
    };
  }, [columns, data]);

  return {
    tableData,
    loading,
    error,
    pagination,
    filtering,
    sorting,
  } as const;
}
